import React, { useEffect, useMemo, useState } from 'react';
import { isSystemUser } from '../../../../../helpers/global';
import useAuth from '../../../../../hooks/useAuth';
import useLocalization from '../../../../../hooks/useLocalization';
import Tabs from '../../../../common/Tabs';
import { Archive, ArrowLeftRightIcon, Calendar, Plus, Star } from 'lucide-react';
import { Button } from '../../../../ui/button';
import { FormCheck } from 'react-bootstrap';
import { Download } from 'react-bootstrap-icons';
import Papa from 'papaparse';

const NewStrategySelector = ({
  unkilledStrategies,
  readOnlyMode,
  readOnlyUserLevel,
  onUpdateStrategyTimeClick,
  onAddNewStrategyClick,
  showArchivedToClient,
  onCemetryClick,
  sortingStartegies,
  sortStrategies,
  updatingStrategy,
  selectedStrategy,
  activeCustomer,
  onEditStrategyClick,
  onDeleteStrategyClick,
  onCloneStrategyClick,
  onSelectStrategyClick,
  fromCustomerPortal,
  onKillStrategyClick,
  onSelectedStrategy,
  onToggleSandboxStrategyClick,
  onBulkCloneStrategiesClick,
  onBulkToggleSandboxStrategiesClick,
  editable,
  disabled,
  onShownToCustomerChange,
  strategy,
  balanceSheetCashflowCsvMeta
}) => {
  const { translate, langCode } = useLocalization();
  const { user } = useAuth();

  const nonSandboxStrategies = useMemo(() => unkilledStrategies.filter(s => !s.isSandboxed), [unkilledStrategies]);

  const sandboxStrategies = useMemo(() => unkilledStrategies.filter(s => s.isSandboxed), [unkilledStrategies]);

  const [showSandboxStrategies, setShowSandboxStrategies] = useState(true);
  const [enableStrategiesRearranging, setEnableStrategiesRearranging] = useState(false);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [checkedStrategyIds, setCheckedStrategyIds] = useState([]);

  const exitMultiSelectMode = () => {
    setMultiSelectMode(false);
    setCheckedStrategyIds([]);
  };

  const onMarkSeveralClick = strategyId => {
    setEnableStrategiesRearranging(false);
    setMultiSelectMode(true);
    setCheckedStrategyIds([strategyId]);
  };

  const onCheckedChange = strategyId => {
    const next = checkedStrategyIds.includes(strategyId)
      ? checkedStrategyIds.filter(id => id !== strategyId)
      : [...checkedStrategyIds, strategyId];
    setCheckedStrategyIds(next);
    if (next.length === 0) {
      setMultiSelectMode(false);
    }
  };

  const getCheckedStrategies = () =>
    (showSandboxStrategies ? sandboxStrategies : nonSandboxStrategies).filter(s =>
      checkedStrategyIds.includes(s._id)
    );

  const multiSelectTabProps = {
    multiSelectMode,
    checkedTabIds: checkedStrategyIds,
    onMarkSeveralClick: !readOnlyMode ? onMarkSeveralClick : undefined,
    onCheckedChange,
    enableStrategiesRearranging: enableStrategiesRearranging && !multiSelectMode
  };

  const hasLessThenTwoSandboxStrategies = sandboxStrategies.length < 2;
  const hasLessThenTwoNonSandboxStrategies = nonSandboxStrategies.length < 2;

  // Handle initial mount - select first strategy of active tab
  useEffect(() => {
    const previouslyChosen = activeCustomer?.strategies?.find(s => s.isSelected);
    if (previouslyChosen) {
      onSelectedStrategy(previouslyChosen);
      return;
    }
    const selectedTabStrategy = showSandboxStrategies ? sandboxStrategies : nonSandboxStrategies;
    if (selectedTabStrategy.length > 0) {
      onSelectedStrategy(selectedTabStrategy[0]);
    } else {
      onSelectedStrategy();
    }
  }, []);

  // Handle selected strategy change - (used in case , when strategy is selected from url)
  useEffect(() => {
    if (selectedStrategy) {
      setShowSandboxStrategies(selectedStrategy?.isSandboxed);
    }
  }, [selectedStrategy]);

  const strategiesActionButtons = [
    {
      icon: Calendar,
      text: translate('update_strategy_time'),
      onClick: onUpdateStrategyTimeClick,
      disabled: false
    },
    {
      icon: Plus,
      text: translate('new_strategy'),
      onClick: () => onAddNewStrategyClick(showSandboxStrategies),
      disabled: false
    },

    {
      icon: Archive,
      text: translate('strategy_archive'),
      onClick: onCemetryClick,
      disabled: !showArchivedToClient
    }
  ];

  const combinedCsv = useMemo(() => {
    if (!balanceSheetCashflowCsvMeta) return null;
    const { balanceSheetCsv, cashflowCsv } = balanceSheetCashflowCsvMeta;

    // Use balance sheet columns as the superset (includes 'appreciation')
    const columns = [...(balanceSheetCsv?.columns || [])];

    // Start with all balance sheet rows
    const combinedDatas = [...(balanceSheetCsv?.datas || [])];

    // Append cashflow rows, ensuring they include 'appreciation' key if column exists
    if (cashflowCsv?.datas?.length) {
      const hasAppreciationColumn = columns?.some(c => c.id === 'appreciation');

      // Section header row for Cashflow
      const headerRow = columns.reduce((acc, c) => {
        acc[c.id] = c.displayName || '';
        return acc;
      }, {});
      headerRow.item = translate('cashflow') || 'Cashflow';
      if (hasAppreciationColumn) headerRow.appreciation = '';
      if (headerRow && headerRow.item) {
        combinedDatas.push(headerRow);
      }

      // Normalize each cashflow row to include all combined columns
      const cashflowRows = cashflowCsv.datas.map(row => {
        const base = columns.reduce((acc, c) => {
          acc[c.id] = '';
          return acc;
        }, {});
        const merged = { ...base, ...row };
        if (hasAppreciationColumn && merged.appreciation === undefined) merged.appreciation = '';
        return merged;
      });
      combinedDatas.push(...cashflowRows);
    }

    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `balance-sheet-cashflow-${dateStr}`;
    return { columns, datas: combinedDatas, filename };
  }, [balanceSheetCashflowCsvMeta, translate]);

  const handleExportCSV = () => {
    if (!combinedCsv) return;

    // Prepare data array with headers
    const headers = combinedCsv.columns.map(col => col.displayName);
    const rows = combinedCsv.datas.map(row => {
      return combinedCsv.columns.map(col => {
        const value = row[col.id];
        // Handle null/undefined values
        if (value === null || value === undefined) return '';
        // Convert to string and preserve commas
        return String(value);
      });
    });

    // Combine headers and rows
    const csvData = [headers, ...rows];

    // Convert to CSV string with semicolon delimiter
    const csv = Papa.unparse(csvData, {
      delimiter: ';',
      quotes: true, // Quote fields to preserve commas and special characters
      quoteChar: '"',
      escapeChar: '"'
    });

    // Create blob and download
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' }); // BOM for Excel UTF-8 support
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${combinedCsv.filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="flex flex-col gap-2.5">
      {(!readOnlyMode || readOnlyUserLevel) && (
        <div className="px-4">
          <div className="flex justify-between items-center overflow-auto">
            <div className="flex items-center gap-2">
              <Button
                variant="hoverOutline"
                size="sm"
                className={`text-xs text-primary shadow-none rounded-full  ${
                  showSandboxStrategies ? 'bg-primary-light' : 'bg-transparent'
                }`}
                style={{ border: '1px solid transparent' }}
                onClick={() => {
                  exitMultiSelectMode();
                  setShowSandboxStrategies(true);
                }}
              >
                <span>{translate('diy_strategies')}</span>
              </Button>

              {(activeCustomer.level >= 2 || readOnlyUserLevel) && (
                <Button
                  variant="hoverOutline"
                  size="sm"
                  className={`text-xs text-primary shadow-none rounded-full ${
                    showSandboxStrategies ? 'bg-transparent' : 'bg-primary-light hover:bg-none'
                  }`}
                  style={{ border: '1px solid transparent' }}
                  onClick={() => {
                    exitMultiSelectMode();
                    setShowSandboxStrategies(false);
                  }}
                >
                  <span>{translate('strategist_strategies')}</span>
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {(readOnlyUserLevel
                ? strategiesActionButtons.filter(b => b.icon === Archive)
                : strategiesActionButtons
              ).map((button, index) => (
                <Button
                  key={index}
                  onClick={button.onClick}
                  disabled={button.disabled}
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                >
                  <button.icon />
                  {button.text}
                </Button>
              ))}
              {!readOnlyMode && !updatingStrategy && (
                <Button
                  size="sm"
                  variant="outline"
                  style={{ border: '1px solid transparent' }}
                  className={`text-xs shadow-none rounded-full transition-colors ${
                    enableStrategiesRearranging
                      ? 'bg-primary text-white hover:bg-primary hover:text-white' // Stay primary on hover
                      : 'bg-transparent text-primary hover:bg-transparent hover:text-primary' // Stay transparent on hover
                  }`}
                  onClick={() => {
                    exitMultiSelectMode();
                    setEnableStrategiesRearranging(prev => !prev);
                  }}
                  disabled={
                    (showSandboxStrategies && hasLessThenTwoSandboxStrategies) ||
                    (!showSandboxStrategies && hasLessThenTwoNonSandboxStrategies)
                  }
                >
                  <ArrowLeftRightIcon />
                  {translate('rearrange_strategies')}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="px-4">
        <div className="border p-2 rounded-lg relative">
          {!showSandboxStrategies ? (
            nonSandboxStrategies.length > 0 ? (
              <Tabs
                scrollable
                disabled={updatingStrategy || sortingStartegies}
                activeTabVariant={'primary'}
                activeTab={selectedStrategy?._id}
                selectedTab={activeCustomer['strategies'].find(s => s.isSelected)?._id}
                tabs={nonSandboxStrategies}
                tabsType="mainbox"
                getTabVariantColor={s => (s.isDefaultStrategy || s.isCreatedByClient ? '#3d77b5' : '')}
                getTabTextColor={s => (s.isDefaultStrategy || s.isCreatedByClient ? 'white' : '')}
                onEditClick={
                  isSystemUser(user?.role)
                    ? t => onEditStrategyClick(activeCustomer['strategies'].find(s => s._id === t))
                    : undefined
                }
                onDeleteClick={
                  isSystemUser(user?.role)
                    ? t => onDeleteStrategyClick(activeCustomer['strategies'].find(s => s._id === t))
                    : undefined
                }
                onCloneClick={t => onCloneStrategyClick(activeCustomer['strategies'].find(s => s._id === t))}
                onSelectClick={
                  !fromCustomerPortal
                    ? t => onSelectStrategyClick(activeCustomer['strategies'].find(s => s._id === t))
                    : undefined
                }
                onMoveToSandboxClick={
                  !fromCustomerPortal &&
                  (t => onToggleSandboxStrategyClick(activeCustomer['strategies'].find(s => s._id === t)))
                }
                onKillClick={
                  showArchivedToClient &&
                  (t => onKillStrategyClick(activeCustomer['strategies'].find(s => s._id === t)))
                }
                onTabClick={t => {
                  onSelectedStrategy(activeCustomer['strategies'].find(s => s._id === t));
                }}
                {...multiSelectTabProps}
                onReorder={reorderedTabs => {
                  sortStrategies([...reorderedTabs, ...sandboxStrategies]);
                }}
                readOnlyMode={readOnlyMode}
                fromCustomerPortal={fromCustomerPortal}
              />
            ) : (
              <h6 className="text-sm text-gray p-1">{translate('nothing_to_show')}</h6>
            )
          ) : sandboxStrategies.length > 0 ? (
            <Tabs
              scrollable
              disabled={updatingStrategy || sortingStartegies}
              activeTabVariant={'primary'}
              activeTab={selectedStrategy?._id}
              selectedTab={activeCustomer['strategies'].find(s => s.isSelected)?._id}
              tabs={sandboxStrategies}
              tabsType="sandbox"
              getTabVariantColor={s => (s.isDefaultStrategy || s.isCreatedByClient ? '#3d77b5' : '')}
              getTabTextColor={s => (s.isDefaultStrategy || s.isCreatedByClient ? 'white' : '')}
              onEditClick={t => onEditStrategyClick(activeCustomer['strategies'].find(s => s._id === t))}
              onDeleteClick={t => onDeleteStrategyClick(activeCustomer['strategies'].find(s => s._id === t))}
              onCloneClick={t => onCloneStrategyClick(activeCustomer['strategies'].find(s => s._id === t))}
              onSelectClick={t => onSelectStrategyClick(activeCustomer['strategies'].find(s => s._id === t))}
              onTabClick={t => {
                onSelectedStrategy(activeCustomer['strategies'].find(s => s._id === t));
              }}
              {...multiSelectTabProps}
              onReorder={reorderedTabs => {
                sortStrategies([...nonSandboxStrategies, ...reorderedTabs]);
              }}
              onMoveToMainboxClick={
                !fromCustomerPortal &&
                (t => onToggleSandboxStrategyClick(activeCustomer['strategies'].find(s => s._id === t)))
              }
              onKillClick={
                showArchivedToClient && (t => onKillStrategyClick(activeCustomer['strategies'].find(s => s._id === t)))
              }
              readOnlyMode={readOnlyMode}
              fromCustomerPortal={fromCustomerPortal}
            />
          ) : (
            <h6 className="text-sm text-gray p-1">{translate('nothing_to_show')}</h6>
          )}
        </div>
        {multiSelectMode && checkedStrategyIds.length > 0 && (
          <div className="flex justify-start mt-2">
            <div className="flex items-center gap-3 px-3 py-2 rounded-md bg-dark text-white text-sm max-w-full">
              <span className="whitespace-nowrap shrink-0">
                {translate('strategies_marked', { count: checkedStrategyIds.length })}
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                {!fromCustomerPortal && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-md border border-white bg-transparent text-white shadow-none hover:bg-white/10 hover:text-white"
                    onClick={() => {
                      onBulkToggleSandboxStrategiesClick?.(getCheckedStrategies());
                      exitMultiSelectMode();
                    }}
                    disabled={updatingStrategy}
                  >
                    {showSandboxStrategies ? translate('move_to_mainbox') : translate('move_to_sandbox')}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-md border border-white bg-transparent text-white shadow-none hover:bg-white/10 hover:text-white"
                  onClick={() => {
                    onBulkCloneStrategiesClick?.(getCheckedStrategies());
                    exitMultiSelectMode();
                  }}
                  disabled={updatingStrategy}
                >
                  {translate('clone')}
                </Button>
                <button
                  type="button"
                  className="px-1 py-1 text-sm text-white bg-transparent border-0 shadow-none hover:opacity-80"
                  onClick={exitMultiSelectMode}
                >
                  {translate('cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex gap-3 text-xs items-center justify-end mt-2 px-4">
        <div className="flex items-center gap-1">
          <Star size={12} strokeWidth={0} fill="#fdb915" />
          <span>{translate('chosen_strategy')}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-4 h-2 rounded-full bg-[#3d77b5] block"></span>
          <span>{translate('created_by_client')}</span>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 w-full p-3 bg-primary text-white text-sm mt-2">
        <div className="flex items-center gap-2">
          {!readOnlyMode && !fromCustomerPortal && selectedStrategy && (
            <div className="flex items-center justify-center p-1 bg-white rounded-full">
              <FormCheck
                id="showToCustomer"
                disabled={disabled}
                onChange={e => onShownToCustomerChange(e.target.checked)}
                className="custom-circular-checkbox flex items-center justify-center"
                checked={strategy.showToCustomer}
              />
            </div>
          )}
          <div className="flex items-center px-2 py-1 rounded">
            <span>{selectedStrategy?.name}</span>
          </div>
        </div>
        {(!readOnlyMode || readOnlyUserLevel) && (
          <Button
            variant="outline"
            size="sm"
            className="d-flex align-items-center gap-1 rounded-full"
            onClick={handleExportCSV}
            disabled={!combinedCsv}
          >
            <Download size={14} />
            <span>{translate('export_csv') || 'Export CSV'}</span>
          </Button>
        )}
      </div>
    </div>
  );
};

export default NewStrategySelector;
